import styled, { createGlobalStyle, FlattenInterpolation, ThemeProps } from 'styled-components';
import { Divider, Modal, Popover } from 'antd';
import { motion } from 'framer-motion';
import media from '@source/constants/media';
import { InfoCircleOutlined } from '@ant-design/icons';

type StyledWrapperProps = {
  wrapperStyles?: FlattenInterpolation<ThemeProps<any>>;
  isSimilarCars?: boolean;
};

export const OverridePopoverStyle = createGlobalStyle`
  .new-disclaimer-popover {
    .ant-popover-inner {
      max-width: 428px;
      border-radius: 30px;
      
      .ant-popover-inner-content {
        padding: 30px 30px;
      }
    }
  }

  .price-popover {
    .ant-popover-inner {
      max-width: 396px;
      border-radius: 30px;
      
      .ant-popover-inner-content {
        padding: 30px 30px;
      }
    }
  }
`;

export const StyledDisclaimer = styled.div`
  .disclaimer-title {
    ${(props) => props.theme.typo.style.sHeader};
    ${(props) => props.theme.typo.familyGoogle.bold};
    color: ${(props) => props.theme.color.primaryBase};
    margin-bottom: 16px;
  }
  .disclaimer-content {
    ${(props) => props.theme.typo.familyGoogle.regular};
    white-space: pre-line;
  }

  .disclaimer-close {
    margin-top: 24px;
    ${(props) => props.theme.typo.style.footNote};
    ${(props) => props.theme.typo.familyGoogle.regular};
    color: #a1a1a1;
    text-align: center;
  }
`;

export const StyledPriceContent = styled.div`
  ${(props) => props.theme.typo.familyGoogle.regular};
  color: #363636;

  .desc {
    margin-bottom: 16px;

    @media screen and (max-width: 576px) {
      margin-top: 12px;
    }
  }

  .content {
    ${(props) => props.theme.typo.style.body};
    font-weight: 600;
    white-space: pre-line;
    padding-left: 16px;
    margin-bottom: 0;
  }

  .close {
    ${(props) => props.theme.typo.style.footNote};
    color: #a1a1a1;
    text-align: center;
    margin-top: 24px;
  }

  .desc-bottom {
    margin-top: 16px;

    @media screen and (min-width: ${({ theme }) => theme.metrics.breakpoints.md}) {
      margin-top: 24px;
    }
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 30px;
  }
`;

export const StyledPhotoWrapper = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  .photo-carousel {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

export const StyledWrapper = styled.a<StyledWrapperProps>`
  display: inline-block;
  position: relative;
  /* box-shadow: ${(props) => props.theme.color.boxShadow.cardRaiseMiddleShadow}; */
  user-select: none;
  margin: 0 auto;
  color: initial;
  background-color: ${(props) => props.theme.color.background};
  ${(props) => props.theme.typo.familyGoogle.regular};

  &:focus,
  &:hover {
    color: inherit;
  }

  .browsing-card-info-icon {
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
    margin-left: 5px;
  }

  .eclipse {
    position: relative;
    display: flex;
    align-items: center;

    &:not(:last-child):after {
      content: '';
      position: absolute;
      right: -12px;
      width: 4px;
      height: 4px;
      background-color: black;
      border-radius: 100%;
      top: calc(50% - 3px);
    }
  }
  border: 1px solid ${(props) => props.theme.color.border}; // #ededed; // set color in theme later
  box-shadow: 0px 4px 15px ${(props) => props.theme.color.borderShadow}; // rgba(33, 33, 33, 0.15); // set color in theme later
  border-radius: 12px;
  width: 100%;
  height: 100%;

  .lazy-load-image-loaded {
    img {
      /* border-top-right-radius: 12px !important;
      border-top-left-radius: 12px !important; */
    }
  }

  ${(props) => props.wrapperStyles}
`;

export const StyledCardContentWrapper = styled.div<{ region: string }>`
  text-align: left;
  padding: 8px 16px 16px 16px;
  border-top: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  position: relative;

  .browsing-card-remaining-date {
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
    margin-left: 4px;
  }

  .browsing-card-tags {
    margin-bottom: 8px;
    display: flex;
    gap: 10px;
  }

  .browsing-card-title {
    ${(props) => props.theme.typo.familyGoogle.semiBold};
    ${(props) => props.theme.typo.style.captionBold};
    color: ${(props) => props.theme.color.onBackgroundHighEmphasis};
    margin-top: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    color: #363636;
    font-size: 16px;
    line-height: 24px;
  }

  .browsing-card-price {
    display: flex;
    align-items: center;
    color: ${(props) => props.theme.color.primaryBase};

    .prefix {
      margin-right: 4px;
    }
  }

  .browsing-card-currency {
    ${(props) => props.theme.typo.familyGoogle.bold};
    font-weight: 700;
    font-size: 30px;
    line-height: 32px;

    @media screen and (max-width: 576px) {
      font-size: 28px;
    }
  }

  .browsing-card-include {
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-size: 10px;
    line-height: 12px;
    color: #363636;
  }

  .browsing-card-price-details {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    flex-wrap: wrap;
    flex-direction: column;
  }

  .bages {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .priceWithGst {
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 20px;
  }

  .priceWithGstTH {
    color: ${(props) => props.theme.color.lightGray};
  }

  .finalPrice {
    display: flex;
    align-items: center;
    gap: 5px;
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};

    .gst {
      font-family: ${(props) => props.theme.typo.familyGoogle.regular};
      font-style: normal;
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
    }
  }

  .browsing-card-secondary-details {
    margin-top: 6px;
    display: flex;
    align-items: center;
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-size: 12px;
    line-height: 13px;
    color: #363636;

    .popover-icon {
      margin-left: 9px;
      display: flex;
      align-items: center;
    }
  }

  .browsing-card-original-price {
    font-size: 10px;
    line-height: 120%;
    font-weight: ${(props) => props.theme.typo.familyGoogle.regular};
    text-decoration: line-through;
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
  }

  .browsing-card-original-price-th {
    font-size: 9px;
    line-height: 20px;
    ${(props) => props.theme.typo.familyGoogle.regular};
  }

  .browsing-card-discount {
    background-color: ${(props) => props.theme.color.background};
    color: ${(props) => props.theme.color.primaryBase};
    border: 1px solid ${(props) => props.theme.color.primaryBase};
    padding: 2px 4px;
    border-radius: 4px;
    border-left: 0;
    position: relative;
    margin-left: 10px !important;
    height: 20px;
    transform-style: preserve-3d;

    &::after {
      content: ' ';
      height: 16px;
      width: 16px;
      background: ${(props) => props.theme.color.background};
      border: 1px solid ${(props) => props.theme.color.primaryBase};
      position: absolute;
      top: 1px;
      left: -5px;
      border-radius: 4px;
      -moz-transform: rotate(45deg) translateZ(-1px);
      -o-transform: rotate(45deg) translateZ(-1px);
      -ms-transform: rotate(45deg) translateZ(-1px);
      -webkit-transform: rotate(45deg) translateZ(-1px);
      transform: rotate(45deg) translateZ(-1px);
    }
  }

  .browsing-card-item-content {
    margin-left: 5px;
    line-height: 1.5;
    color: ${(props) => props.theme.color.onBackgroundHighEmphasis};
  }

  .browsing-card-instalment {
    font-size: 12px;
    line-height: 20px;
  }

  .secondary-price-my {
    margin-left: 8px;
  }

  .secondary-price-th {
    margin-left: 2px;
  }

  .vatPercent {
    font-size: 8px;
  }

  .browsing-card-listed-price {
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-size: 10px;
    line-height: 14px;
  }
`;

export const StyledCardInfo = styled.div<{ isMSSG: boolean }>`
  align-items: center;
  ${(props) => props.theme.typo.familyGoogle.regular};
  background-color: ${(props) => props.theme.color.background};
  margin-top: 8px;
  font-size: 12px;
  line-height: 20px;

  .spec-item {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 4px;

    .spec-item-label {
      width: ${(props) => (props.isMSSG ? '160px' : '120px')};
      font-weight: 500;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
    }

    .spec-item-value {
    }
  }
`;

export const StyledDivider = styled(Divider)`
  margin: 11px 0 14px;
  border-top: 1px solid #d4d4d4;
`;

export const StyledBrandNewBage = styled.div`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  padding: 6px 11px;
  background: #de1c22;
  border-radius: 5px;
  color: #fff;
  font-size: 12px;
  line-height: 7px;
`;

export const StyledCarPricePopover = styled(Popover)`
  .infoIcon {
    cursor: pointer;
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
  }
`;

export const StyledInfoIcon = styled(InfoCircleOutlined)`
  font-size: 18px;
  color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
  margin-left: 4px;
`;

export const StyledFrom = styled.span`
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
`;

export const StyledPrimaryValueBuyNew = styled.span`
  position: relative;
  top: 3px;
  font-size: 30px;
  font-weight: 700;
  line-height: 32px;
  ${(props) => props.theme.typo.familyGoogle.bold};

  @media screen and (max-width: 576px) {
    font-size: 28px;
  }
`;

export const StyledAllIn = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 25px;
`;

export const StyledPrimaryValue = styled.span`
  font-size: 30px;
  font-weight: 700;
  line-height: 32px;
  ${(props) => props.theme.typo.familyGoogle.bold};

  @media screen and (max-width: 576px) {
    font-size: 28px;
  }
`;

export const StyledIncludeNote = styled.span`
  ${(props) => props.theme.typo.familyGoogle.regular};
  color: #a1a1a1;
  font-size: 10px;
  line-height: 12px;
  font-style: italic;
`;

export const StyledInstalmentValue = styled.span`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  margin-left: 4px;
`;
